/* provis-a11y-runtime.js (2026-04-08, deep-audit revision)
   Runtime accessibility remediation — targets axe-core violations that
   CSS alone cannot solve. Safe to load on every page. No dependencies. */
(function () {
  'use strict';

  function a11yFix(root) {
    root = root || document;
    if (!root.querySelectorAll) return;

    /* ----------------------------------------------------------------
       aria-hidden-focus
       Shopify product-title and cart-drawer ship duplicate
       <a aria-hidden="true" href="..."> that stay in the tab order.
       ---------------------------------------------------------------- */
    root.querySelectorAll('[aria-hidden="true"] a[href], [aria-hidden="true"] button, a[aria-hidden="true"][href], button[aria-hidden="true"]').forEach(function (el) {
      el.setAttribute('tabindex', '-1');
    });

    /* ----------------------------------------------------------------
       aria-allowed-role — Shopify "More" overflow uses
       <button role="listitem"> which is invalid.
       ---------------------------------------------------------------- */
    root.querySelectorAll('button[role="listitem"]').forEach(function (b) {
      b.removeAttribute('role');
    });

    /* ----------------------------------------------------------------
       list rule — <ul part="list"> in Horizon's <overflow-list>
       holds non-<li> children (accordion-custom, role=presentation).
       Stripping the list semantic with role="none" is the
       semantically-correct fix: this UL is a layout container, not
       real list content for assistive tech.
       ---------------------------------------------------------------- */
    root.querySelectorAll('ul[part="list"]').forEach(function (ul) {
      if (!ul.hasAttribute('role')) ul.setAttribute('role', 'none');
    });
    root.querySelectorAll('ul[part="list"] > [role="presentation"]').forEach(function (el) {
      el.removeAttribute('role');
    });

    /* ----------------------------------------------------------------
       scrollable-region-focusable — Horizon <slideshow-slides> is a
       scrollable region with tabindex="-1" and no focusable children.
       Make it focusable and label it.
       ---------------------------------------------------------------- */
    root.querySelectorAll('slideshow-slides').forEach(function (s) {
      var slides = s.querySelectorAll('[role="group"], .slideshow__slide, slide-element').length;
      if (s.getAttribute('tabindex') !== '0') s.setAttribute('tabindex', '0');
      if (!s.hasAttribute('role')) s.setAttribute('role', 'region');
      if (!s.hasAttribute('aria-roledescription')) s.setAttribute('aria-roledescription', 'carousel');
      if (!s.hasAttribute('aria-label')) {
        // Inherit a useful label from a wrapping <a aria-label> if present
        var wrap = s.closest('a[aria-label]');
        s.setAttribute('aria-label', wrap ? (wrap.getAttribute('aria-label') + ' image carousel') : 'Image carousel — use arrow keys');
      }
    });

    /* ----------------------------------------------------------------
       landmark-unique — duplicate announcement-bar/nav landmarks.
       ---------------------------------------------------------------- */
    var seen = {};
    function labelLandmark(el, base) {
      if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby')) return;
      seen[base] = (seen[base] || 0) + 1;
      el.setAttribute('aria-label', seen[base] === 1 ? base : base + ' ' + seen[base]);
    }
    root.querySelectorAll('aside.announcement-bar').forEach(function (el) {
      labelLandmark(el, 'Announcements');
    });
    root.querySelectorAll('accordion-custom .menu__details nav, menu-drawer nav').forEach(function (el) {
      labelLandmark(el, 'Menu section');
    });
    root.querySelectorAll('accordion-custom .details-content[role]').forEach(function (el) {
      labelLandmark(el, 'Menu panel');
    });

    /* ----------------------------------------------------------------
       heading-order — promote stray h3s that lead a section.
       ---------------------------------------------------------------- */
    root.querySelectorAll('section, .shopify-section').forEach(function (sec) {
      var headings = sec.querySelectorAll('h1, h2, h3, h4, h5, h6');
      if (!headings.length) return;
      var minLevel = 7;
      headings.forEach(function (h) {
        var l = parseInt(h.tagName[1], 10);
        if (l < minLevel) minLevel = l;
      });
      if (minLevel === 3) {
        headings.forEach(function (h) {
          if (h.tagName === 'H3' && !h.hasAttribute('aria-level')) {
            h.setAttribute('role', 'heading');
            h.setAttribute('aria-level', '2');
          }
        });
      }
    });

    /* ----------------------------------------------------------------
       Skip-link target — ensure <main> has id="MainContent".
       ---------------------------------------------------------------- */
    var main = root.querySelector ? root.querySelector('main') : null;
    if (main && !main.id) main.id = 'MainContent';

    /* ----------------------------------------------------------------
       html lang fallback
       ---------------------------------------------------------------- */
    if (root === document && !document.documentElement.getAttribute('lang')) {
      document.documentElement.setAttribute('lang', 'en');
    }

    /* ----------------------------------------------------------------
       Icon-only buttons without accessible name
       ---------------------------------------------------------------- */
    root.querySelectorAll('button:not([aria-label]):not([aria-labelledby])').forEach(function (b) {
      if (b.textContent.trim()) return;
      if (b.querySelector('img[alt]:not([alt=""])')) return;
      if (!b.querySelector('svg, i.icon, [class*="icon"]')) return;
      var cls = (b.className || '') + '';
      var guess =
        /close|dismiss/i.test(cls) ? 'Close' :
        /cart|bag/i.test(cls)      ? 'Cart' :
        /search/i.test(cls)        ? 'Search' :
        /menu|hamburger|nav/i.test(cls) ? 'Menu' :
        /account|user/i.test(cls)  ? 'Account' :
        /prev|back|previous/i.test(cls) ? 'Previous' :
        /next|forward/i.test(cls)  ? 'Next' :
        'Button';
      b.setAttribute('aria-label', guess);
    });

    /* ----------------------------------------------------------------
       <img> without alt attribute — assume decorative.
       Only set if missing entirely (don't overwrite empty alt="" intent).
       ---------------------------------------------------------------- */
    root.querySelectorAll('img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
      img.setAttribute('role', 'presentation');
    });

    /* ----------------------------------------------------------------
       Form inputs without label/aria-label — best-effort fix
       ---------------------------------------------------------------- */
    root.querySelectorAll('input:not([type="hidden"]):not([aria-label]):not([aria-labelledby])').forEach(function (inp) {
      if (inp.id) {
        var lab = root.querySelector('label[for="' + CSS.escape(inp.id) + '"]');
        if (lab) return;
      }
      var ph = inp.getAttribute('placeholder');
      if (ph) inp.setAttribute('aria-label', ph);
    });

    /* ----------------------------------------------------------------
       Menu-drawer / cart-drawer — ESC close + focus return.
       Wire once per element via dataset flag.
       ---------------------------------------------------------------- */
    root.querySelectorAll('cart-drawer, menu-drawer, header-drawer').forEach(function (d) {
      if (d.dataset.pvA11yWired) return;
      d.dataset.pvA11yWired = '1';
      d.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          var closeBtn = d.querySelector('button[aria-label*="lose" i], button.close-button, [data-close], dialog-close-button');
          if (closeBtn) closeBtn.click();
        }
      });
    });
  }

  function run() { a11yFix(document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', function (e) { a11yFix(e.target); });
  document.addEventListener('shopify:section:select', function (e) { a11yFix(e.target); });

  // Persistent observer — Shopify cart/menu drawers, slideshows, and
  // overflow lists mutate continuously. We keep a long-lived observer
  // (debounced) so axe-fixed state survives client-side re-renders.
  var pending = false;
  var mo = new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      a11yFix(document);
    });
  });
  function startObserver() {
    if (document.body) {
      mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'tabindex', 'role'] });
    }
  }
  if (document.body) startObserver();
  else document.addEventListener('DOMContentLoaded', startObserver);

  // Re-run on common drawer-open events Shopify dispatches
  ['cart:open','drawer:open','dialog:open','menu:open'].forEach(function (ev) {
    document.addEventListener(ev, function () { setTimeout(run, 50); });
  });
})();
